import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, ArrowLeft, RefreshCw } from 'lucide-react';
import { syncMerchantPaymentLinkStatus } from '../../api/loan-action-creator';
import './razorpay-return.css';

const STORAGE_KEY = 'carepay_pending_payment_link_id';
const PLINK_RE = /plink_[A-Za-z0-9]+/;

export const getPaymentReturnUrl = () =>
  `${window.location.origin}/payments/razorpay/return/`;

export const rememberPendingPaymentLink = (paymentLinkId) => {
  if (!paymentLinkId) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, paymentLinkId);
  } catch (e) {
    /* ignore */
  }
};

const readPendingPaymentLink = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) || '';
  } catch (e) {
    return '';
  }
};

const clearPendingPaymentLink = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
};

/** Pull plink_xxx from path, query, or any appended URL fragment */
const extractPlinkId = (value = '') => {
  if (!value) return '';
  try {
    const decoded = decodeURIComponent(String(value));
    const match = decoded.match(PLINK_RE);
    return match ? match[0] : '';
  } catch (e) {
    const match = String(value).match(PLINK_RE);
    return match ? match[0] : '';
  }
};

const formatRupees = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return num.toLocaleString('en-IN');
};

/**
 * Razorpay payment-link return page.
 * Redirect URL ends with paymentLinkId, e.g.
 *   /payments/razorpay/return/plink_TK4N71SX2sj9fL
 */
const RazorpayReturn = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [syncing, setSyncing] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const paymentLinkId = useMemo(() => {
    const fromPathParam = extractPlinkId(params.paymentLinkId);
    const fromPathname = extractPlinkId(window.location.pathname);
    const fromHref = extractPlinkId(window.location.href);
    const fromSearch =
      extractPlinkId(searchParams.get('razorpay_payment_link_id')) ||
      extractPlinkId(searchParams.get('paymentLinkId')) ||
      extractPlinkId(searchParams.get('payment_link_id')) ||
      extractPlinkId(searchParams.get('plink_id')) ||
      extractPlinkId(window.location.search);

    return (
      fromPathParam ||
      fromPathname ||
      fromHref ||
      fromSearch ||
      readPendingPaymentLink() ||
      ''
    );
  }, [params.paymentLinkId, searchParams]);

  const razorpayStatus = (
    searchParams.get('razorpay_payment_link_status') ||
    searchParams.get('status') ||
    ''
  ).toLowerCase();

  const razorpayPaymentId =
    searchParams.get('razorpay_payment_id') ||
    searchParams.get('payment_id') ||
    '';

  const runSync = (linkId) => {
    if (!linkId) {
      setSyncing(false);
      setError('Payment link ID missing. Open Credits & Ledger and sync manually.');
      return;
    }

    setSyncing(true);
    setError(null);

    syncMerchantPaymentLinkStatus(
      linkId,
      (data) => {
        setSyncing(false);
        setResult(data);
        if ((data.status || '').toLowerCase() === 'paid') {
          clearPendingPaymentLink();
        }
      },
      (err) => {
        setSyncing(false);
        const apiMsg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message;
        setError(apiMsg || 'Failed to confirm payment status');
      }
    );
  };

  useEffect(() => {
    runSync(paymentLinkId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentLinkId]);

  const status = (result?.status || razorpayStatus || '').toLowerCase();
  const creditsPosted = (result?.creditsPosted || '').toLowerCase();
  const amountLabel = formatRupees(result?.amountRupees);

  const goToCredits = () => {
    navigate('/dashboard', { replace: true, state: { openScansCredits: true } });
  };

  let tone = 'pending';
  let title = 'Confirming payment';
  let detail = 'Please wait while we verify your payment and load credits.';

  if (syncing) {
    tone = 'pending';
    title = 'Confirming payment';
    detail = 'Syncing with Razorpay and posting credits to your ledger…';
  } else if (error) {
    tone = 'error';
    title = 'Could not confirm payment';
    detail = error;
  } else if (status === 'paid' && creditsPosted === 'yes') {
    tone = 'success';
    title = 'Payment successful';
    detail = amountLabel
      ? `₹${amountLabel} received. Credits have been added to your ledger.`
      : 'Credits have been added to your ledger.';
  } else if (status === 'paid' && creditsPosted === 'processing') {
    tone = 'pending';
    title = 'Payment received';
    detail = 'Crediting your ledger… this may take a moment.';
  } else if (status === 'paid' && creditsPosted === 'failed') {
    tone = 'error';
    title = 'Payment received, credit failed';
    detail = 'Your payment went through, but credits could not be posted. Tap retry.';
  } else if (status === 'paid') {
    tone = 'success';
    title = 'Payment successful';
    detail = amountLabel
      ? `₹${amountLabel} received. Credits will appear in your ledger shortly.`
      : 'Payment received. Credits will appear in your ledger shortly.';
  } else if (['cancelled', 'expired', 'failed'].includes(status)) {
    tone = 'error';
    title = 'Payment not completed';
    detail = `Status: ${status}. You can try again from Credits & Ledger.`;
  } else if (!syncing) {
    tone = 'pending';
    title = 'Payment pending';
    detail = status
      ? `Current status: ${status}. If you already paid, tap retry.`
      : 'If you already paid, tap retry to refresh status.';
  }

  return (
    <div className="rzp-return-page">
      <div className={`rzp-return-card ${tone}`}>
        <div className="rzp-return-icon" aria-hidden="true">
          {syncing ? (
            <Loader2 size={36} className="rzp-spin" />
          ) : tone === 'success' ? (
            <CheckCircle2 size={36} />
          ) : tone === 'error' ? (
            <XCircle size={36} />
          ) : (
            <Loader2 size={36} className="rzp-spin" />
          )}
        </div>

        <h1>{title}</h1>
        <p>{detail}</p>

        {(paymentLinkId || razorpayPaymentId || result?.paymentId) && (
          <dl className="rzp-return-meta">
            {paymentLinkId && (
              <>
                <dt>Payment link</dt>
                <dd title={paymentLinkId}>{paymentLinkId}</dd>
              </>
            )}
            {(result?.paymentId || razorpayPaymentId) && (
              <>
                <dt>Payment ID</dt>
                <dd>{result?.paymentId || razorpayPaymentId}</dd>
              </>
            )}
            {result?.clinicCode && (
              <>
                <dt>Clinic</dt>
                <dd>{result.clinicCode}</dd>
              </>
            )}
            {creditsPosted && (
              <>
                <dt>Credits</dt>
                <dd>{creditsPosted}</dd>
              </>
            )}
          </dl>
        )}

        <div className="rzp-return-actions">
          {!syncing && (
            <button
              type="button"
              className="rzp-return-secondary"
              onClick={() => runSync(paymentLinkId)}
              disabled={!paymentLinkId}
            >
              <RefreshCw size={16} />
              Retry sync
            </button>
          )}
          <button type="button" className="rzp-return-primary" onClick={goToCredits}>
            <ArrowLeft size={16} />
            Back to Credits
          </button>
        </div>
      </div>
    </div>
  );
};

export default RazorpayReturn;
